import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { ElementAction } from '../../elements/elementAction';
import { ContentPlanRootState as S } from '../../elements/contentPlan/store';
import { Functions } from '../../types/types';
import { ItemStatus } from '../../enums/ItemStatus';
import { RelationshipTypeId } from '../../enums/RelationshipTypeId';
import { ContentType } from '../../enums/ContentType';
import { DocInfo } from '../../types/DocInfo';
import {
  getAndFlattenCustomFieldData,
  getFilteredDataSourcesAndProjects,
  getItemStatusId,
  isRemovedOrInactive,
  isUniMDChild
} from '../../utils';
import { Metadata, Schema } from '../../types';
import { TriggerLocation } from '../../enums/TriggerLocation';
import { ToasterWithLinkWrapper } from '../Toaster/toaster';
import URLManager from '../../utils/urlManager';
import { updateMetadataFormState } from '../MetadataForm/metadataFormReducer';
import { ItemType } from '../../enums/ItemType';
import { Role } from '../../enums/Role';
import { WorkflowSavePayload } from '../../types/ConfigureWorkflowTypes';
import { ConfigureWorkflowType } from '../../enums/ConfigureWorkflowType';
import { UpdateOptions } from '../../enums/UpdateOptions';
import { ApiResponseMessage } from '../../utils/constants';

type ItemInfo = DocInfo & { permissions: { [x in UpdateOptions]: boolean } };

type LoggedUser = {
  employeeId: number;
  hasAdminAccess: boolean;
  hasContentPlannerAccess: boolean;
};
const loadItemInfo =
  (itemId: number): ElementAction<Functions, S, any, Promise<ItemInfo>> =>
  async (dispatch, getState, { getAPI }) => {
    const { app, form, planning, accessControl } = getState();
    const data = form?.data;
    const refId = data?.props?.refId;
    const title = data?.props?.title;
    const workspaceRefId = planning?.selectedWorkspace?.props?.refId;
    const workspaceRoles =
      accessControl?.permissions?.[workspaceRefId]?.roles || [];
    const appUser = {
      employeeId: app.user.employeeId,
      hasAdminAccess: app.user.roles?.includes(Role.admin),
      hasContentPlannerAccess: workspaceRoles.includes(Role.planner)
    };
    const { getDocInfo } = getAPI();
    const docInfo = await getDocInfo(`query {
        item(itemId: ${itemId}) {
          itemId
          googleDocumentId
          contentTypeId
          itemStatusId
          expiryDate
          ownerEmployeeId
          managerEmployeeId
          contextualizationOfDocument {
            relationshipTypeId
          }
          translationOfDocument {
            relationshipTypeId
          }
          contentTypeConfig {
            ...ContentTypeConfigFields 
          }
        }
      }`);
    return {
      ...docInfo,
      refId,
      title,
      permissions: {
        [UpdateOptions.UNI]: !disableUNI(docInfo),
        [UpdateOptions.LightUpdate]: !disableLightUpdate(appUser, docInfo, data)
      }
    };
  };

const disableUNI = (docInfo: DocInfo) => {
  const isComplete = docInfo?.itemStatusId == ItemStatus.cebComplete;
  const isRetractedTemporary =
    docInfo?.itemStatusId == ItemStatus.RetractedTemporary;
  const isContextualization =
    docInfo?.contextualizationOfDocument &&
    docInfo?.contextualizationOfDocument.length > 0;
  const isPerspectiveContext =
    isContextualization &&
    docInfo?.contextualizationOfDocument[0].relationshipTypeId ==
      RelationshipTypeId.PerspectiveContextualization;
  const isVariantContext =
    isContextualization &&
    docInfo?.contextualizationOfDocument[0].relationshipTypeId ==
      RelationshipTypeId.VariantContextualization;
  const isIpContentType =
    docInfo?.contentTypeId == ContentType.INNOVATION_PROFILE;
  const isMarketDefinitionContentType =
    docInfo?.contentTypeId == ContentType.MARKET_DEFINITION;
  const isRPContentType = docInfo?.contentTypeId == ContentType.Radar_Profile;
  const isDisableReportHighlight =
    docInfo?.reportHighlightOfDocument &&
    docInfo?.reportHighlightOfDocument.length > 0;
  const isRemoved = isRemovedOrInactive(docInfo?.itemStatusId);
  const isFullTranslationOfMarketDefinition =
    isMarketDefinitionContentType &&
    docInfo?.translationOfDocument &&
    docInfo?.translationOfDocument[0].relationshipTypeId ==
      RelationshipTypeId.Full_Translation;
  const isEFSContentType =
    docInfo?.contentTypeId == ContentType.Executive_FastStart;
  return (
    isRemoved ||
    (isContextualization &&
      !isPerspectiveContext &&
      !isVariantContext &&
      !isComplete) ||
    isDisableReportHighlight ||
    ((isIpContentType || isMarketDefinitionContentType || isRPContentType) &&
      !isComplete) ||
    isRetractedTemporary ||
    isFullTranslationOfMarketDefinition ||
    isEFSContentType
  );
};

const disableLightUpdate = (
  loggedUser: LoggedUser,
  docInfo: DocInfo,
  data: Partial<Metadata>
) => {
  const isTeamManagerOfLeadAuthor =
    docInfo?.managerEmployeeId == loggedUser.employeeId ? true : false;
  const isFirstTakeLeadAuthor =
    docInfo?.contentTypeId === ContentType.First_Take &&
    docInfo?.ownerEmployeeId === loggedUser.employeeId;

  return (!loggedUser.hasAdminAccess &&
    !isTeamManagerOfLeadAuthor &&
    !loggedUser.hasContentPlannerAccess &&
    !isFirstTakeLeadAuthor) ||
    (docInfo?.itemStatusId != ItemStatus.Published &&
      docInfo?.itemStatusId != ItemStatus.Complete) ||
    (docInfo?.expiryDate && new Date() > new Date(docInfo?.expiryDate)) ||
    !docInfo?.contentTypeConfig?.workflow?.lightUpdate ||
    docInfo?.itemStatusId == (ItemStatus.RetractedTemporary as any)
    ? true
    : false;
};

const createUNI = (data: Partial<Metadata>, schemaForm: Schema) => {
  const parentStatus = data?.props?.status[0];
  const parentStatusId = parentStatus?.props?.id;
  const parentStatusText = parentStatus?.props?.description;
  const customFieldData = getAndFlattenCustomFieldData(data, schemaForm);

  const { filteredDataSources, filteredProjects, filteredDataSourceItems } =
    getFilteredDataSourcesAndProjects(data);

  const uni = {
    props: {
      ...customFieldData,
      itemType: ItemType.content,
      title: data?.props?.title,
      summary: data?.props?.summary,
      function: data?.props?.function,
      role: data?.props?.role,
      primaryQuestion: data?.props?.primaryQuestion,
      additionalQuestions: data?.props?.additionalQuestions,
      additionalFunctions: data?.props?.additionalFunctions,
      additionalRoles: data?.props?.additionalRoles,
      researchModel: data?.props?.researchModel,
      useCase: data?.props?.useCase,
      contentType: data?.props?.contentType,
      contentSubtype: data?.props?.contentSubtype,
      author: data?.props?.author,
      coAuthors: data?.props?.coAuthors,
      market: data?.props?.market,
      solDeliveryMethod: data?.props?.solDeliveryMethod,
      solHostingApproach: data?.props?.solHostingApproach,
      solPurchaseModel: data?.props?.solPurchaseModel,
      buyingCenter: data?.props?.buyingCenter,
      lifecyclePhase: data?.props?.lifecyclePhase,
      uniOf: [{ id: data?.id, props: data?.props, labels: data?.labels }],
      language: data?.props?.language,
      dataSource: filteredDataSources,
      dataSourceItem: filteredDataSourceItems,
      project: filteredProjects,
      bigRocks: data?.props?.bigRocks
    },
    triggerLocation: TriggerLocation.UNI,
    parentStatusId,
    parentStatusText
  } as Partial<Metadata>;
  if (isUniMDChild(uni)) {
    uni.props.configureWorkflowType = ConfigureWorkflowType.Branded;
    uni.props.status = [
      {
        props: {
          refId: 'status:10',
          id: ItemStatus.Concept_Review,
          description: 'Concept Review'
        }
      }
    ];
    uni.props.additionalFunctions = null;
    uni.props.additionalRoles = null;
    uni.props.additionalTopicCategory = null;
    uni.props.additionalTaxonomyTag = null;
    uni.props.rolePriority = null;
    uni.props.priorityJourneyStep = null;
  }
  return uni;
};

const reloadItemStatus =
  (
    itemId: number,
    curStatusId: number,
    maxPull: number = 1,
    onStatusChanged?: () => void,
    onStatusNotChanged?: () => void
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    if (maxPull <= 0) {
      onStatusNotChanged?.();
      return;
    }
    const { schema, data } = getState().form;
    if (data?.props?.itemId == itemId) {
      const dataStatusId = getItemStatusId(data);
      if (dataStatusId == curStatusId) {
        const { getDataDetail } = getAPI();
        const searchResp = await getDataDetail(
          data.props.refId,
          schema?.props?.type
        );
        if (searchResp?.data?.length == 1) {
          const latestData = searchResp.data[0];
          const latestStatusId = getItemStatusId(latestData);
          if (latestStatusId && latestStatusId != dataStatusId) {
            data.props.status = latestData.props.status;
            dispatch(updateMetadataFormState({ data: { ...data } }));
            onStatusChanged?.();
          } else {
            await new Promise(resolve => setTimeout(resolve, 500));
            await dispatch(
              reloadItemStatus(
                itemId,
                curStatusId,
                maxPull - 1,
                onStatusChanged,
                onStatusNotChanged
              )
            );
          }
        }
      }
    }
  };

const createLightUpdate =
  (
    itemInfo: ItemInfo,
    data: Partial<Metadata>,
    toaster?: ToasterWithLinkWrapper,
    configureLightUpdateWorkflow?: WorkflowSavePayload
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { createLightUpdate } = getAPI();

    const { form, planning, view, app } = getState();
    const { selectedWorkspace } = planning;
    const itemData = data;
    const empCd = app?.user?.employeeId;
    const {
      requestCreativeHelp,
      configureLightUpdateWorkflowType,
      configureLightUpdateWorkflowSkipIds
    } = configureLightUpdateWorkflow || {};

    const applyWorkflowStateToForm = () => {
      const formData = getState().form?.data;
      if (formData?.props?.refId == data.props.refId) {
        formData.props.requestCreativeHelp = requestCreativeHelp;
        formData.props.configureLightUpdateWorkflowType =
          configureLightUpdateWorkflowType;
        formData.props.configureLightUpdateWorkflowSkipIds =
          configureLightUpdateWorkflowSkipIds;
        dispatch(updateMetadataFormState({ data: { ...formData } }));
      }
    };
    const successMessages = [
      `Light Update successfully started for `,
      {
        url: URLManager.generateUrl(selectedWorkspace, {
          v: view.selected,
          id: itemData?.props?.refId
        }),
        params: { id: itemData?.props?.refId },
        text: itemData?.props?.title || '<Title Not Defined>'
      }
    ];
    const errorMessages = [
      `Error trying to initiate a Light Update for `,
      {
        url: URLManager.generateUrl(planning.selectedWorkspace, {
          v: view.selected,
          id: itemData?.props?.refId
        }),
        params: { id: itemData?.props?.refId },
        text: itemData?.props?.title || '<Title Not Defined>'
      },
      `. Please contact CPP Help if the issue persists.`
    ];
    try {
      toaster?.show('Starting Light Update...');
      const response = await createLightUpdate({
        itemId: itemInfo?.itemId,
        googleDocumentId: itemInfo?.googleDocumentId,
        eventType: `CREATE_LIGHT_UDPATE`,
        configureWorkflow: {
          ...configureLightUpdateWorkflow,
          configureWorkflowByEmpCd: empCd
        }
      });
      if (!!response.success) {
        toaster.success({ messages: successMessages });
        await dispatch(
          reloadItemStatus(
            itemInfo.itemId,
            itemInfo.itemStatusId,
            10,
            applyWorkflowStateToForm
          )
        );
      } else {
        console.error('Invalid response', response);
        throw new Error('Invalid Response');
      }
      return response;
    } catch (e) {
      if ((e as any)?.message == ApiResponseMessage.EndpointRequestTimedOut) {
        await dispatch(
          reloadItemStatus(
            itemInfo.itemId,
            itemInfo.itemStatusId,
            120,
            () => {
              applyWorkflowStateToForm();
              toaster?.success({
                messages: successMessages
              });
            },
            () => {
              toaster?.error({
                messages: errorMessages
              });
            }
          )
        );
        return;
      }
      if (toaster) {
        toaster.error({ messages: errorMessages });
      }
    }
  };

const updateLightUpdateWorkflow =
  (
    data: Partial<Metadata>,
    {
      requestCreativeHelp,
      configureWorkflowType,
      configureWorkflowSkipIds
    }: WorkflowSavePayload
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    if (!data?.props?.itemId || !data?.props?.refId) {
      throw new Error('Invalid item data for light update workflow');
    }

    const empCd = getState().app?.user?.employeeId;
    const payload: Partial<Metadata> = {
      id: data.id,
      labels: data.labels,
      props: {
        refId: data.props.refId,
        itemId: data.props.itemId,
        requestCreativeHelp,
        configureWorkflowByEmpCd: empCd,
        configureLightUpdateWorkflowType: configureWorkflowType,
        configureLightUpdateWorkflowSkipIds: configureWorkflowSkipIds
      }
    };

    const { updateLightUpdateConfigureWorkflow } = getAPI();
    await updateLightUpdateConfigureWorkflow(payload, getItemStatusId(data));

    const formData = getState().form?.data;
    if (formData?.props?.refId == data.props.refId) {
      formData.props.requestCreativeHelp = requestCreativeHelp;
      formData.props.configureLightUpdateWorkflowType = configureWorkflowType;
      formData.props.configureLightUpdateWorkflowSkipIds =
        configureWorkflowSkipIds;
      dispatch(updateMetadataFormState({ data: { ...formData } }));
    }
  };

export const useActions = () => {
  const dispatch: ThunkDispatch<S, any, any> = useDispatch();
  return {
    loadItemInfo: async (itemId: number) => dispatch(loadItemInfo(itemId)),
    createLightUpdate: async (
      itemInfo: ItemInfo,
      data: Partial<Metadata>,
      toaster?: ToasterWithLinkWrapper,
      configureLightUpdateWorkflow?: WorkflowSavePayload
    ) =>
      dispatch(
        createLightUpdate(itemInfo, data, toaster, configureLightUpdateWorkflow)
      ),
    updateLightUpdateWorkflow: async (
      data: Partial<Metadata>,
      payload: WorkflowSavePayload
    ) => dispatch(updateLightUpdateWorkflow(data, payload)),
    createUNI
  };
};
